import { Box } from '@mui/material';
import { useEffect, useState, useContext, SetStateAction, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { AppState, AppDispatch, AppStore } from '../store';
import {
  AutoSelectedAssignmentsDialog,
  ConflictDialog,
  SectionLayout,
  UnsupportedBrowser,
} from '.';
import {
  getSelectableSections,
  UrlHelper,
  onVariableChange,
  getLanguageCode,
  setOptionalItemsToState,
  checkIsMarkedOptional,
  getCurrencyCode,
  getProductId,
  showConfigurationSummaryTabDrawer,
} from '../services';
import { SessionStore } from '../services/SessionStore';
import { useTranslation } from 'react-i18next';
import {
  EVariableType,
  ESessionStore,
  EChangeType,
  EUrlParams,
  EErrorTypes,
  DefaultUserSettings,
  EMessagesType,
  ProductScope,
  ESalesTextType,
} from '../data/Constants';
import { countryCodes } from '../localization';
import WarningIcon from '@mui/icons-material/Warning';
import { AppAuthContext } from '../services/Contexts';
import { IsDefaultFlowInSessionStore } from '../services/SessionStoreHelperFunctions';
import { isFeatureActiveAndVisible, isFeatureVisible } from '../services/ClaimsHelper';
import { getUserDetails } from '../store/states/UserSlice';
import {
  setApplicationSettings,
  setIsConfigurationChanged,
} from '../store/states/ApplicationSettingsSlice';
import { resetError } from '../store/states/ErrorSlice';
import { getUserPreferences, setUserPreferences } from '../store/states/UserPreferencesSlice';
import {
  configure,
  getCurrencyList,
  getPMLanguages,
  getPriceListMap,
  getProductImages,
  getPropertyAssociations,
  getSalesText,
  getShortSalesTextAndPrice,
  getSubModelData,
  getViewList,
  loadConfiguration,
  setLoaderMessage,
  setOptionalItemsApi,
  setSearchValues,
} from '../store/states/ConfigurationSlice';
import { getClaims } from '../store/states/ClaimsDataSlice';
import { setMessage, setOpenPopoverOnLoad } from '../store/states/MessagesSlice';
import Disclaimer from './Disclaimer';
import { SaveAndClose } from './SaveAndClose';
import classNames from 'classnames';
import { TFunction } from 'i18next';
import type {
  IApplicationSettings,
  IConfigurationVariable,
  ICurrentSectionReturnType,
  IExtendedConfigureResponse,
  ILanguage,
  IValueType,
  ILastChange,
  IPriceListMap,
  IExtendedConfigureResponseOptional,
  IConfigureRequest,
  IClaimsData,
  INamed,
  IConfiguration,
  ISection,
  IMarketingInfoParams,
  IConfigureApiCallProps,
  IUserSettings,
  ISetLanguageProps,
  IGetCurrencyProps,
  ILoadProductProps,
  IUserDetails,
  IUserSettingsResponse,
  IProductAssignments,
  ILoadConfiguration,
} from '../../types';
import type {
  IApiErrorShape,
  IConfigureApiResponse,
  ILoadConfigurationApiResponse,
} from '../../types/IApiTypes';
import { getDeltaAssignmentsByRule } from '../services/AutoSelectedDialogHelperFunctions';

const changeOptionaItems = (
  value: IValueType,
  variable: IConfigurationVariable,
  lastChange: ILastChange,
  request: IConfigureRequest,
  configuration: IExtendedConfigureResponse
) => {
  let itemKey: IValueType;
  const vrblType = variable.variableType;
  if (lastChange.type === EChangeType.Remove) {
    itemKey = value;
  } else if (lastChange.type === EChangeType.Change || lastChange.type === EChangeType.Add) {
    //For Multiselect lastChange.type will be EChangeType.Add and for Single select lastChange.type will be EChangeType.Change
    if (
      (vrblType === EVariableType.Number || vrblType === EVariableType.String) &&
      lastChange.value === ''
    ) {
      itemKey = variable.id;
    } else {
      itemKey = value;
    }
  }

  const flag = checkIsMarkedOptional(itemKey as string, variable);
  if (flag) {
    // Create a new configuration object (don't mutate Redux state)

    const updatedConfiguration = updateOptionalItemsToConfiguration(
      configuration,
      itemKey as string,
      false,
      variable
    );

    // adding optionalItems in request payload on uncheck or clear of feature
    addOptionalItemsToRequest(request, updatedConfiguration);
  }
};

/**
 * To get the current section to display in configurator
 * @param {IExtendedConfigureResponse} configuration the configuration state
 * @param {IApplicationSettings} applicationSettings the application settings state
 * @returns {ICurrentSectionReturnType} the current section to display and view availability for the model
 */

export function getCurrentSection(
  configuration: IExtendedConfigureResponse,
  applicationSettings: IApplicationSettings
): ICurrentSectionReturnType {
  if (!configuration.data?.sections) {
    return { section: null, viewUnavailable: false };
  }
  const { activeTab } = applicationSettings;
  const { sections, viewUnavailable, assignmentsByRule, searchresult } =
    getSelectableSections(configuration);

  if (!sections.length) {
    return { section: null, viewUnavailable, currentAssignmentsByRule: assignmentsByRule };
  }
  // To check if all the sections in the current model are isItemsAvailable/hidden
  const hiddenAll = sections.filter((x) => x.isItemsAvailable === true);
  if (hiddenAll.length === sections.length) {
    return {
      section: null,
      viewUnavailable,
      selectableItemsUnavailable: true,

      currentAssignmentsByRule: assignmentsByRule,
      searchresult,
    };
  }
  return {
    section: sections[activeTab],
    viewUnavailable,

    currentAssignmentsByRule: assignmentsByRule,
    searchresult,
  };
}

const configureApiCall = (param: IConfigureApiCallProps) => {
  const { request, token, applicationSettings, lastChange, dispatch } = param;
  dispatch(setLoaderMessage('configure'));
  dispatch(
    configure({
      request: request,
      token: token,
      language: getLanguageCode(),
      lastChange: lastChange,
    })
  ).then((response: { payload: IConfigureApiResponse | IApiErrorShape }) => {
    const res = response.payload;
    dispatch(setLoaderMessage(null));
    if ('error' in res && !applicationSettings.isConfigurationChanged) {
      // if error on 1st time configuration changed
      dispatch(setIsConfigurationChanged({ isConfigurationChanged: false }));
    } else if (
      !('error' in res) &&
      !applicationSettings.isConfigurationChanged &&
      (res.configuration?.removedAssignments?.variableAssignments?.length ?? 0) > 0
    ) {
      //if not error & changing for 1st time but there are conflicts
      dispatch(setIsConfigurationChanged({ isConfigurationChanged: false }));
    } else {
      //if error but user is not changing for 1st time means there is change in configuration so enable button
      dispatch(setIsConfigurationChanged({ isConfigurationChanged: true }));
    }
  });
};

async function getPriceListMapData(countryCode: string, token: string, dispatch: AppDispatch) {
  const storedPriceListMap = SessionStore.get(ESessionStore.PriceListMap);
  let priceListMap = await (storedPriceListMap ?? dispatch(getPriceListMap({ token: token })));
  if (!storedPriceListMap) {
    SessionStore.set(ESessionStore.PriceListMap, priceListMap);
  }
  priceListMap = priceListMap?.payload?.find(
    (priceMap: IPriceListMap) => priceMap.country === countryCode
  )?.map;
  priceListMap = priceListMap ?? countryCode;
  return priceListMap;
}

function getConfigurationId(): string {
  return (
    UrlHelper.getSearchParameter(EUrlParams.ConfigurationId) ??
    SessionStore.get(ESessionStore.ConfigurationId)
  );
}

function getUserMarket(
  configuration: IExtendedConfigureResponseOptional,
  applicationSettings: IApplicationSettings
) {
  const countryCode = configuration?.assignments?.find((res) => {
    return res.variableId === ProductScope.MarketId;
  })?.['value'];

  //returns true when the Country/Market given by User is loaded in applicationSettings
  return countryCode === applicationSettings.currency.countryCode;
}

/**
 * This method call the api to get the submodel marketing information
 * @param {IExtendedConfigureResponse} configuration the configuration state
 * @param {IApplicationSettings} applicationSettings application settings state
 * @param {string} token api token
 * @param {AppDispatch} dispatch dispatch the getPrice api
 * @returns {void}
 */

async function getSubmodelInfoOnInitialLoad(
  configuration: IExtendedConfigureResponse,
  applicationSettings: IApplicationSettings,
  token: string,
  dispatch: AppDispatch,
  modelId: string,
  dispatchedModelIdsRef: Set<string>
): Promise<void> {
  if (configuration.savedConfiguration && getUserMarket(configuration, applicationSettings)) {
    const path = modelId?.split('.') || [];
    const currentModelId = path.pop();
    const countVariables = configuration?.contextData?.countVariables;
    const modelIds: string[] = [];
    const keys = Object.keys(countVariables || {});

    keys.forEach((variable) => {
      const assignedValue = countVariables?.[variable]?.values?.find((v) => v.assigned);
      if (assignedValue && Number(assignedValue.value) > 0) {
        modelIds.push(variable);
      }
    });
    const mandatoryPromises: Promise<any>[] = [];
    const pdmPromises: Promise<any>[] = [];
    const optionalPromises: Promise<any>[] = [];
    // Looping the submodels and call the api to get the marketing info for the submodels
    //Price, ShortSalestext and Property association Api will call if submodel quantity is greater than zero
    //Image and longSalesText Api will call only for current submodel
    if (modelIds.length > 0) {
      //✅ STEP 1: First loop - Only dispatch PDM calls
      modelIds.forEach((mid) => {
        const id = mid.split('.').pop()?.split('_')[0] ?? '';
        pdmPromises.push(
          dispatch(
            getPropertyAssociations({
              token: token,
              packageId: getProductId(),
              productId: id ?? '',
              propertyDefiniton: configuration.propertyDefinition,
            })
          )
        );
      });
      // ✅ STEP 2: Wait for all PDM promises to complete
      await Promise.allSettled(pdmPromises);

      modelIds.forEach((mid) => {
        const id = mid.split('.').pop()?.split('_')[0] ?? '';
        const language: string = getLanguageCode();
        // Skip if already dispatched or if data already exists in state
        const alreadyDispatched = dispatchedModelIdsRef.has(id);

        if (!alreadyDispatched && SessionStore.get(ESessionStore.PriceListMap)) {
          // Mark as dispatched to prevent duplicate calls
          dispatchedModelIdsRef.add(id);

          mandatoryPromises.push(
            dispatch(
              getShortSalesTextAndPrice({
                salesTextShort: {
                  language: language,
                  token: token,
                  showError: false,
                },
                price: {
                  token: token,
                  countryCode: applicationSettings.priceListMap,
                  currencyCode: applicationSettings.currency.currencyCode,
                },
                modelId: id,
              })
            )
          );
        }

        //Call the Image and long Salestext API when reload the page from any submodel
        if (currentModelId === id) {
          optionalPromises.push(
            dispatch(getProductImages({ token: token, modelId: id })),
            dispatch(
              getSalesText({
                language,
                token,
                modelId: id,
                apiType: ESalesTextType.Long,
                showError: false,
              })
            )
          );
        }
      });
      await Promise.allSettled(mandatoryPromises);
      Promise.allSettled(optionalPromises).catch((error) => {
        console.error('Background image/long sales text loading failed:', error);
      });
    }
  }
}

function getClassSelector(applicationSettings: IApplicationSettings) {
  return applicationSettings.pageSize.isExtraSmall ? 0 : 1;
}

function addOptionalItemsToRequest(
  request: IConfigureRequest,
  configuration: IExtendedConfigureResponse
) {
  request.optionalItems = configuration.savedConfiguration?.optionalItems
    ? [...configuration.savedConfiguration.optionalItems.keys()]
    : [];
}

function changeOptional(
  configuration: IExtendedConfigureResponse,
  token: string,
  dispatch: AppDispatch
) {
  const configurationId = getConfigurationId();
  dispatch(
    setOptionalItemsApi({
      token: token,
      configurationId: configurationId,
      optionalItems: configuration.savedConfiguration?.optionalItems
        ? [...configuration.savedConfiguration.optionalItems.keys()]
        : [],
    })
  );
}

function updateOptionalItemsToConfiguration(
  configuration: IExtendedConfigureResponse,
  id: string,
  value: boolean,
  variable: IConfigurationVariable
): IExtendedConfigureResponse {
  if (!configuration.savedConfiguration) {
    return configuration;
  }

  // Create a new Map with the updated optionalItems
  const updatedOptionalItems = new Map(configuration.savedConfiguration.optionalItems || new Map());

  // Get the updated items from setOptionalItemsToState
  const newOptionalItems = setOptionalItemsToState(id, value, variable, updatedOptionalItems);

  // Create a new savedConfiguration object with the updated optionalItems
  const updatedSavedConfiguration: ILoadConfiguration = {
    ...configuration.savedConfiguration,
    optionalItems: newOptionalItems,
  };

  // Create a new configuration object (don't mutate Redux state)
  return {
    ...configuration,
    savedConfiguration: updatedSavedConfiguration,
  };
}

function setPMLanguages(
  response: { pmLanguages: [{ languages: ILanguage[] }] | null } | null,
  userSettings: IUserSettings,
  loading: boolean,
  setLanguageSelection: (value: SetStateAction<boolean>) => void,
  dispatch: AppDispatch
) {
  const userLanguage = response?.pmLanguages?.[0]?.languages.find(
    (lang) => lang.code === userSettings.language
  );
  if (
    !loading &&
    (userLanguage !== undefined || response?.pmLanguages?.[0]?.languages.length === 1)
  ) {
    setLanguageSelection(true);
  }
  if (userLanguage === undefined) {
    setLanguageSelection(true);
    dispatch(
      setUserPreferences({
        language: userSettings.language,
        showCode: userSettings.showCode,
      })
    );
  }
}

/**
 *Call the API to get the root model salestext,Prices, images and Currency etc.
 * @param {IMarketingInfoProps} params input params object
 * @returns { Promise<void> } Call the APi to get the root model information
 */

async function getPMMarketingInfo(params: IMarketingInfoParams) {
  const { bcpLangCode, token, productModel, currencyCode, priceListMap, hasSubmodel, dispatch } =
    params;
  const optionalPromises = [];
  if (hasSubmodel) {
    await dispatch(
      getSubModelData({
        token,
        productId: productModel,
        packageId: productModel,
        language: bcpLangCode,
      })
    );
  }
  dispatch(
    getShortSalesTextAndPrice({
      salesTextShort: {
        language: bcpLangCode,
        token: token,
        showError: false,
      },
      price: {
        token: token,
        countryCode: priceListMap,
        currencyCode: currencyCode,
      },
      modelId: productModel,
    })
  );
  optionalPromises.push(
    dispatch(getProductImages({ token: token, modelId: productModel })),
    dispatch(
      getSalesText({
        language: bcpLangCode,
        token: token,
        modelId: productModel,
        apiType: ESalesTextType.Long,
        showError: false,
      })
    )
  );

  // Run in background without blocking UI - fire and forget pattern
  Promise.allSettled(optionalPromises).catch((error) => {
    console.error('Background image/long sales text loading failed:', error);
  });
}

function setDefaultUserSettings(dispatch: AppDispatch) {
  dispatch(setUserPreferences({ ...DefaultUserSettings, isUserSettingsDefault: true }));
  return { data: DefaultUserSettings };
}

const setUserSettingsAndLanguages = async (langSettingProps: ISetLanguageProps) => {
  const { languageSelection, loading, setLanguageSelection, token, configurationId, dispatch } =
    langSettingProps;
  if (!languageSelection && !loading) {
    let userSettings: IUserSettings;
    const lang =
      UrlHelper.getSearchParameter(EUrlParams.Language) ?? SessionStore.get(ESessionStore.Language);
    const contry =
      UrlHelper.getSearchParameter(EUrlParams.Country) ?? SessionStore.get(ESessionStore.Country);
    if (lang && contry) {
      dispatch(
        setUserPreferences({
          language: lang + '-' + contry,
          showCode: true,
        })
      );
      userSettings = {
        language: lang + '-' + contry,
        showCode: true,
      };
    } else {
      const res = await dispatch(getUserPreferences({ token: token }));
      userSettings = (res.payload as IUserSettingsResponse)?.data;
      if (!userSettings) {
        userSettings = setDefaultUserSettings(dispatch).data;
      }
    }
    await dispatch(getPMLanguages({ configurationId: configurationId, token: token }))
      .unwrap()
      .then((response: { pmLanguages: [{ languages: ILanguage[] }] } | null) => {
        setPMLanguages(response, userSettings, loading, setLanguageSelection, dispatch);
      });
  }
};

const langCodeValue = (
  languageCode: string,
  urlLanguageCode: string,
  urlLanguage: string,
  t: TFunction,
  dispatch: AppDispatch
) => {
  if (languageCode !== urlLanguageCode) {
    //To set message when invalid language is passed
    dispatch(
      setMessage({
        text: urlLanguage
          ? t('errorMessages.invalidLanguage', {
              urlLanguage: urlLanguageCode,
              fallbackLanguage: languageCode,
            })
          : t('errorMessages.noLanguage', { fallbackLanguage: languageCode }),
        type: EMessagesType.Info,
      })
    );
  }
};

const currencyCodeValue = (props: IGetCurrencyProps) => {
  const { isCurrencyValid, urlCurrency, claimsData, t, dispatch } = props;
  if (isCurrencyValid) {
    return urlCurrency;
  } else if (
    !isFeatureVisible(claimsData.featureFlags.ListPrice) &&
    isFeatureVisible(claimsData.featureFlags.Currency)
  ) {
    dispatch(
      setMessage({
        text: urlCurrency
          ? t('errorMessages.invalidCurrencyLPDisabled', { currency: urlCurrency })
          : t('errorMessages.noCurrency'),
        type: EMessagesType.Info,
      })
    );
  } else if (isFeatureVisible(claimsData.featureFlags.ListPrice)) {
    dispatch(
      setMessage({
        text: urlCurrency
          ? t('errorMessages.invalidCurrency', { currency: urlCurrency })
          : t('errorMessages.noCurrency'),
        type: EMessagesType.Info,
      })
    );
  }
  return 'NaN';
};

const popoverOnLoad = (
  languageCode: string | INamed[],
  urlLanguageCode: string,
  isCurrencyValid: boolean,
  dispatch: AppDispatch
) => {
  if (languageCode !== urlLanguageCode || !isCurrencyValid) {
    dispatch(setOpenPopoverOnLoad(true));
  }
};
export const handleCurrencyCodeChange = (
  isCurrencyValid: boolean,
  sessionCurrency: string,
  priceListMap: string,
  countryCurrency: { currency: string[] } | null,
  claimsData: IClaimsData,
  t: TFunction,
  dispatch: AppDispatch
) => {
  let currencyCode = '';
  if (sessionCurrency === null) {
    //when no currency is passed from the url
    currencyCode = getCurrencyCode(priceListMap, countryCurrency); //gets the default currency for the country
    if (
      isFeatureVisible(claimsData.featureFlags.ListPrice) ||
      isFeatureVisible(claimsData.featureFlags.Currency)
    ) {
      dispatch(
        setMessage({
          text: t('errorMessages.noCurrency', { fallbackCurrency: currencyCode }),
          type: EMessagesType.Info,
        })
      );
    }
  } else {
    //if sessionCurrency is invalid currencyCode will be NaN else it is sessionCurrency
    currencyCode = currencyCodeValue({
      isCurrencyValid,
      urlCurrency: sessionCurrency,
      claimsData,
      t,
      dispatch,
    });
  }
  return currencyCode;
};

/**
 * Extract country code from configuration response
 */
function extractCountryCode(response: any): string {
  const userAssignment = response.data.userAssignments.find(
    (assignment: { variableId: string }) => assignment.variableId === ProductScope.MarketId
  );
  return userAssignment?.value || '';
}

/**
 * Handle currency determination for non-default flow
 */
function handleNonDefaultFlowCurrency(
  languageCode: string,
  sessionCurrency: string,
  priceListMap: string,
  countryCurrency: { currency: string[] } | null,
  claimsData: IClaimsData,
  t: TFunction,
  dispatch: AppDispatch
): string {
  // Cache session store reads
  const urlLanguage = SessionStore.get(ESessionStore.Language);
  const urlCountry = SessionStore.get(ESessionStore.Country);
  const urlLanguageCode = `${urlLanguage}-${urlCountry}`;

  langCodeValue(languageCode, urlLanguageCode, urlLanguage, t, dispatch);

  const isCurrencyValid = Object.values(countryCodes)
    .map((x) => x.currencyCode)
    .includes(sessionCurrency);

  const currencyCode = handleCurrencyCodeChange(
    isCurrencyValid,
    sessionCurrency,
    priceListMap,
    countryCurrency,
    claimsData,
    t,
    dispatch
  );

  popoverOnLoad(languageCode, urlLanguageCode, isCurrencyValid, dispatch);

  return currencyCode;
}

/**
 * Determine currency code based on flow type and session data
 */
async function determineCurrencyCode(
  languageCode: string,
  priceListMap: string,
  claimsData: IClaimsData,
  token: string,
  t: TFunction,
  dispatch: AppDispatch
): Promise<string> {
  // Cache session currency read
  const sessionCurrency = SessionStore.get(ESessionStore.Currency);

  // Get country currency if price list map exists
  const countryCurrency = SessionStore.get(ESessionStore.PriceListMap)
    ? await dispatch(getCurrencyList({ token, countryCode: priceListMap })).unwrap()
    : null;

  if (!IsDefaultFlowInSessionStore()) {
    return handleNonDefaultFlowCurrency(
      languageCode,
      sessionCurrency,
      priceListMap,
      countryCurrency,
      claimsData,
      t,
      dispatch
    );
  }

  return getCurrencyCode(priceListMap, countryCurrency);
}

/**
 * Fetch initial product data in parallel for better performance
 */
async function fetchInitialProductData(
  configurationId: string,
  languageCode: string,
  token: string,
  dispatch: AppDispatch,
  navigate: (path: string, options?: { replace?: boolean; state?: any }) => void
): Promise<{ configResponse: ILoadConfigurationApiResponse | null }> {
  const res = await dispatch(getViewList({ configurationId: configurationId, token: token }));
  const viewResponse = res.payload as any;
  if (viewResponse.error?.code === 400 || viewResponse.error?.code === 404) {
    dispatch(resetError());
    navigate('/Error', {
      replace: true,
      state: {
        type: EErrorTypes.InvalidConfiguration,
        code: viewResponse.error?.code,
        message: viewResponse.error?.message,
      },
    });
    return { configResponse: null };
  }

  const configResult = await dispatch(
    loadConfiguration({
      configurationId: configurationId,
      languageCode: languageCode,
      token: token,
      viewId:
        UrlHelper.getSearchParameter(EUrlParams.ViewId) ?? SessionStore.get(ESessionStore.ViewId),
    })
  );
  const productModel = getProductId();
  const configResponse = configResult.payload as ILoadConfigurationApiResponse | IApiErrorShape;
  if ('error' in configResponse || !configurationId) {
    return {
      configResponse: null,
    };
  }

  // Fetch property associations after configuration is loaded
  await dispatch(
    getPropertyAssociations({
      token,
      packageId: productModel,
      productId: productModel,
      propertyDefiniton: [],
    })
  );

  return { configResponse };
}

/**
 * Main product loading function - optimized for performance and maintainability
 */
const loadProduct = async (params: ILoadProductProps) => {
  const {
    languageSelection,
    loading,
    token,
    setLanguageSelection,
    setLoading,
    i18n,
    navigate,
    claimsData,
    t,
    dispatch,
  } = params;

  // For PWA on edge, configurationId is read from session store as fallback
  const configurationId = getConfigurationId();

  // Set user settings and languages (non-blocking)
  setUserSettingsAndLanguages({
    languageSelection,
    loading,
    setLanguageSelection,
    token,
    configurationId,
    dispatch,
  });

  // Early return if language not selected
  if (!languageSelection) {
    return;
  }

  setLoading(true);
  if (languageSelection) {
    const languageCode = getLanguageCode();
    i18n.changeLanguage(languageCode.replace('-', '_'));

    // Fetch initial data in parallel
    const { configResponse } = await fetchInitialProductData(
      configurationId,
      languageCode,
      token,
      dispatch,
      navigate
    );

    if (!configResponse) {
      return;
    }
    // Extract data from response
    const countryCode = extractCountryCode(configResponse);
    const productModel = getProductId();
    const hasSubmodel = configResponse.data.modelContext?.subModels?.length > 0;
    const priceListMap = await getPriceListMapData(countryCode, token, dispatch);
    const currencyCode = await determineCurrencyCode(
      languageCode,
      priceListMap,
      claimsData,
      token,
      t,
      dispatch
    );
    const currency = {
      countryCode,
      currencyCode,
      locale: countryCodes[countryCode]['locale'],
    };
    dispatch(setApplicationSettings({ currency, priceListMap }));
    getPMMarketingInfo({
      bcpLangCode: languageCode,
      token,
      productModel,
      currencyCode,
      priceListMap,
      hasSubmodel,
      dispatch,
    });
  }
};

function changeValue(params: {
  request: IConfigureRequest | null;
  requestType?: string;
  configuration: IExtendedConfigureResponse;
  token: string;
  applicationSettings: IApplicationSettings;
  lastChange: ILastChange;
  value: IValueType;
  variable: IConfigurationVariable;
  dispatch: AppDispatch;
}) {
  const {
    request,
    requestType,
    configuration,
    token,
    applicationSettings,
    lastChange,
    value,
    variable,
    dispatch,
  } = params;
  if (!request) {
    return;
  }
  if (requestType !== 'optional') {
    changeOptionaItems(value, variable, lastChange, request, configuration);
  }
  if (requestType === 'optionalCheck') {
    // adding optionalItems in request payload on check of Opt col when feature is not checked
    addOptionalItemsToRequest(request, configuration);
  }
  configureApiCall({ request, token, applicationSettings, lastChange, dispatch });
}

const handleTabClose = (event: BeforeUnloadEvent): void => {
  event.preventDefault();
};

function addConfirmationListener() {
  window.addEventListener('beforeunload', handleTabClose, { capture: true });
}

function removeConfirmationListener() {
  window.removeEventListener('beforeunload', handleTabClose, { capture: true });
}

/**
 * This function used to fetch the user details, claims and clear the pricelist from session
 * @param {string} token
 * @param {userDetails} userDetails
 * @param {claimsData} claimsData
 * @param {AppDispatch} dispatch
 */

function getPrimaryDetails(
  token: string,
  userDetails: IUserDetails,
  claimsData: IClaimsData,
  dispatch: AppDispatch
): void {
  //to remove PriceListMap key from SessionStore if page is reloaded
  if (
    (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming).type === 'reload'
  ) {
    SessionStore.remove(ESessionStore.PriceListMap);
  }
  if (token && Object.keys(userDetails).length === 0) {
    dispatch(getUserDetails({ token: token }));
  }
  if (token && !claimsData.claims) {
    dispatch(getClaims({ token: token }));
  }
}

//This function returns the message if current view is unavailable for the product model
function renderInValidViewMessage(
  viewUnavailable: boolean,
  selectableItemsUnavailable: boolean | undefined,
  t: TFunction
) {
  return (
    <Box className="root text-left align-top justify-left">
      {viewUnavailable && !selectableItemsUnavailable
        ? t('configurator.viewUnavailable')
        : t('configurator.noSelectableSections')}
    </Box>
  );
}

//This function returns the boolean value based on the below checks
function checkInValidView(
  busy: boolean,
  currentSection: ISection | null,
  configurationData: IConfiguration | null | undefined
) {
  return !busy && !currentSection && configurationData;
}

//This function check and return the boolean to load the Product
function checkLoadProduct(
  configuration: IExtendedConfigureResponse,
  loading: boolean,
  languageSelection: boolean
) {
  return (
    (!configuration.savedConfiguration && !loading) ||
    languageSelection ||
    configuration.acceptedChanges
  );
}

/**
 * Computes the delta between previous and current rule assignments and determines dialog visibility
 * @param {IProductAssignments} previous - Previous assignments by rule state
 * @param {IProductAssignments} current - Current assignments by rule from getSelectableSections
 * @param {boolean} hasConflict - Whether there is an active configuration conflict
 * @param {boolean} showAutoSelections - Whether auto-selection dialog preference is enabled
 * @returns {{ differences: IProductAssignments; count: number; shouldShowDialog: boolean }}
 */
function computeAssignmentsDelta(
  previous: IProductAssignments,
  current: IProductAssignments,
  hasConflict: boolean,
  showAutoSelections: boolean
): { differences: IProductAssignments; count: number; shouldShowDialog: boolean } {
  const { differences, count } = getDeltaAssignmentsByRule(previous, current);
  if (count === 0) {
    return { differences: {}, count: 0, shouldShowDialog: false };
  }
  return { differences, count, shouldShowDialog: !hasConflict && showAutoSelections };
}

//This function returns the Disclaimer and SaveAndClose  component
function renderSaveAndClose(
  configuration: IExtendedConfigureResponse,
  isDefaultFlow: boolean,
  show: boolean
) {
  return (
    <Box className="disclaimer-addproduct">
      {!isDefaultFlow && <Disclaimer />}
      {!isDefaultFlow && !show && <SaveAndClose configuration={configuration} />}
    </Box>
  );
}

/**
 * Renders the currently selected Section and is responsible to handle the assignment functionality
 * @returns {JSX.Element} the configurator component with the currently selected section
 */
export const Configurator = () => {
  const token = useContext(AppAuthContext);
  const { i18n, t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [languageSelection, setLanguageSelection] = useState(false);

  // State for currentAssignmentsByRule tracking
  const [assignmentsByRule, setAssignmentsByRule] = useState<IProductAssignments>({});
  const [assignedByRuleDifferences, setAssignedByRuleDifferences] = useState<{
    differences: IProductAssignments;
    count: number;
  }>({ differences: {}, count: 0 });
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showAutoSelectDialog, setShowAutoSelectDialog] = useState(false);
  const navigate = useNavigate();
  const [serachParams] = useSearchParams();
  const model = serachParams.get(EUrlParams.Model) ?? '';
  // Track modelIds that have been dispatched to prevent duplicate API calls across re-renders
  const dispatchedModelIdsRef = useRef<Set<string>>(new Set());
  const dispatch = useDispatch<AppDispatch>();

  //selector
  const claimsData = AppStore.getState().claimsData;
  const busy = useSelector((state: AppState) => state.busy);
  const applicationSettings = useSelector((state: AppState) => state.applicationSettings);
  const userDetails = useSelector((state: AppState) => state.user.details);
  const configuration = useSelector((state: AppState) => state.configuration);
  const userLanguage = useSelector((state: AppState) => state.userSettings.language);

  const {
    section: currentSection,
    viewUnavailable,
    selectableItemsUnavailable,
    currentAssignmentsByRule,
    searchresult,
  } = getCurrentSection(configuration, applicationSettings);

  useEffect(() => {
    if (applicationSettings.isConfigurationChanged) {
      addConfirmationListener();
    } else {
      removeConfirmationListener();
    }
  }, [applicationSettings.isConfigurationChanged]);

  useEffect(() => {
    return () => {
      // Anything in here is fired on component unmount.
      window.removeEventListener('beforeunload', handleTabClose, { capture: true });
    };
  }, []);

  useEffect(() => {
    if (checkLoadProduct(configuration, loading, languageSelection)) {
      //load product directly if no product was loaded before or user aceepts the changes
      loadProduct({
        languageSelection,
        loading,
        token,
        setLanguageSelection,
        setLoading,
        i18n,
        navigate,
        claimsData,
        t,
        dispatch,
      });
    }
  }, [languageSelection, configuration.refresh]);

  useEffect(() => {
    getPrimaryDetails(token, userDetails, claimsData, dispatch);
  }, [token]);

  // Clear dispatched model IDs cache when language or currency changes
  useEffect(() => {
    dispatchedModelIdsRef.current.clear();
  }, [
    userLanguage,
    applicationSettings.currency.currencyCode,
    applicationSettings.currency.countryCode,
  ]);

  useEffect(() => {
    //call price for submodel on page load
    getSubmodelInfoOnInitialLoad(
      configuration,
      applicationSettings,
      token,
      dispatch,
      model,
      dispatchedModelIdsRef.current
    );
  }, [configuration.lastChange, applicationSettings.currency, userLanguage]);

  useEffect(() => {
    dispatch(setSearchValues({ searchresult: searchresult }));
  }, [JSON.stringify(searchresult)]);

  // Callback to handle accepted changes from ConflictDialog (moved before early return)
  const handleAcceptChanges = useCallback((acceptedDifferences?: IProductAssignments) => {
    // Only update assignmentsByRule if there are any accepted differences.
    // It ensures that we only update the state when there is actually something to update (i.e., at least one difference was accepted by the user).
    if (acceptedDifferences && Object.keys(acceptedDifferences).length > 0) {
      setAssignmentsByRule(acceptedDifferences);
    }
    setShowAutoSelectDialog(false);
  }, []);

  // Track auto-selected rule assignments and show dialog when new rule-based changes are detected.
  // Runs on every configuration change (lastChange) to compare previous vs current rule assignments.
  useEffect(() => {
    // Guard: skip processing if the ASItemsDialog feature flag is disabled
    if (!isFeatureActiveAndVisible(claimsData.featureFlags.ASItemsDialog)) {
      return;
    }

    // Reset dialog visibility at the start of each evaluation cycle
    setShowAutoSelectDialog(false);

    // Check if any rule-based assignments exist in the current configuration
    const hasAssignments =
      currentAssignmentsByRule && Object.keys(currentAssignmentsByRule).length > 0;

    // No rule assignments present — clear any previously tracked differences
    if (!hasAssignments) {
      setAssignedByRuleDifferences({ differences: {}, count: 0 });
      return;
    }

    // First load: capture the baseline snapshot of rule assignments without computing a delta
    if (isInitialLoad) {
      setAssignmentsByRule(currentAssignmentsByRule);
      setIsInitialLoad(false);
      return;
    }

    // Subsequent user changes: compute the delta between stored and current rule assignments
    if (configuration.lastChange !== null) {
      const result = computeAssignmentsDelta(
        assignmentsByRule,
        currentAssignmentsByRule,
        !!configuration.conflict, // true if there is an active conflict preventing auto-select dialog
        applicationSettings.showAutoSelections // user preference: whether to show the dialog at all
      );
      // Update the tracked differences (could be empty if no new items were auto-selected)
      setAssignedByRuleDifferences({ differences: result.differences, count: result.count });
      // Show the auto-selected assignments dialog only when new items exist and no conflicts block it
      if (result.shouldShowDialog) {
        setShowAutoSelectDialog(true);
      }
    }
  }, [configuration.lastChange]);

  if (!configuration || !applicationSettings) {
    return null;
  }

  const onChange = (variable: IConfigurationVariable, value: IValueType, requestType?: string) => {
    setShowAutoSelectDialog(false);
    const lastChange = { type: '', variableId: variable.id, value, valueBefore: null };
    const request = onVariableChange(configuration, variable, value, lastChange);
    changeValue({
      request,
      requestType,
      configuration,
      token,
      applicationSettings,
      lastChange,
      value,
      variable,
      dispatch,
    });
  };

  // To display message when all the families in the section are hidden
  if (selectableItemsUnavailable && configuration.isDataLoaded) {
    return (
      <Box className="root text-left align-center d-flex noSelectableMessage">
        <WarningIcon className="warningIcon" /> &nbsp; {t('configurator.noSelectableItems')}
      </Box>
    );
  }
  if (checkInValidView(busy, currentSection, configuration.data)) {
    return renderInValidViewMessage(viewUnavailable, selectableItemsUnavailable, t);
  }

  const onOptionalChange = (id: string, value: boolean, variable: IConfigurationVariable) => {
    // Create a new configuration object (don't mutate Redux state)
    const updatedConfiguration = updateOptionalItemsToConfiguration(
      configuration,
      id,
      value,
      variable
    );

    changeOptional(updatedConfiguration, token, dispatch);
  };

  const show = showConfigurationSummaryTabDrawer(applicationSettings);
  const isDefaultFlow = IsDefaultFlowInSessionStore();

  const classSelector = getClassSelector(applicationSettings);
  const padding = ['p-10px', 'p-15px'];

  return (
    <Box className={`configurator-root root ${padding[classSelector]}`}>
      <Box
        className={classNames({
          ['configurator-content']: isDefaultFlow,
          ['configurator-content-with-disclaimer']:
            !isDefaultFlow && !applicationSettings.pageSize.isMedium,
          ['medium-configurator-content']: !isDefaultFlow && applicationSettings.pageSize.isMedium,
        })}
      >
        {currentSection && (
          <SectionLayout
            section={currentSection}
            onChange={onChange}
            onOptionalChange={onOptionalChange}
            isDefaultFlow={isDefaultFlow}
          />
        )}
      </Box>
      {renderSaveAndClose(configuration, isDefaultFlow, show)}
      {/* Only render ConflictDialog when there are conflicts */}
      {configuration.conflict && (
        <ConflictDialog
          assignedByRuleDifferences={assignedByRuleDifferences}
          onAccept={handleAcceptChanges}
        />
      )}
      {/* Only show AutoSelectedAssignmentsDialog when there are rule assignments but NO conflicts */}
      {!busy && !configuration.conflict && showAutoSelectDialog && (
        <AutoSelectedAssignmentsDialog
          assignedByRuleDifferences={assignedByRuleDifferences}
          onAccept={() => handleAcceptChanges(assignedByRuleDifferences.differences)}
        />
      )}
      <UnsupportedBrowser />
    </Box>
  );
};
